import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API_BASE } from "./AuthContext";
import { ChevronDown, User as UserIcon, X } from "lucide-react";

// Module-level cache so we hit /team/members once per page load
let CACHED_MEMBERS = null;
let CACHED_PROMISE = null;
const MEMBER_LISTENERS = new Set();

async function loadMembers() {
  if (CACHED_MEMBERS) return CACHED_MEMBERS;
  if (CACHED_PROMISE) return CACHED_PROMISE;
  CACHED_PROMISE = axios
    .get(`${API_BASE}/team/members`)
    .then((r) => {
      CACHED_MEMBERS = r.data || [];
      MEMBER_LISTENERS.forEach((fn) => fn(CACHED_MEMBERS));
      return CACHED_MEMBERS;
    })
    .finally(() => { CACHED_PROMISE = null; });
  return CACHED_PROMISE;
}

export function useTeamMembers() {
  const [members, setMembers] = useState(CACHED_MEMBERS || []);
  useEffect(() => {
    let alive = true;
    if (!CACHED_MEMBERS) loadMembers().then((m) => { if (alive) setMembers(m); });
    const listener = (m) => { if (alive) setMembers(m); };
    MEMBER_LISTENERS.add(listener);
    return () => { alive = false; MEMBER_LISTENERS.delete(listener); };
  }, []);
  return members;
}

function initials(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (src.slice(0, 2) || "??").toUpperCase();
}

export function AssigneeAvatar({ user, size = 22 }) {
  if (!user) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full border border-dashed border-[var(--border)] text-[var(--ink-3)]"
        style={{ width: size, height: size }}
        title="Unassigned"
      >
        <UserIcon size={Math.max(10, size - 12)} />
      </span>
    );
  }
  if (user.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name}
        style={{ width: size, height: size }}
        className="rounded-full object-cover"
      />
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-[var(--ink)] text-[var(--paper)] font-mono"
      style={{ width: size, height: size, fontSize: Math.max(9, size / 2.4) }}
      title={user.name || user.email}
    >
      {initials(user.name, user.email)}
    </span>
  );
}

/**
 * Compact assignee picker. Renders a chip-style button; click opens a dropdown
 * of org members. Calls onChange(userIdOrNull) when the user picks one.
 *
 * Props:
 *   value: user_id | null
 *   onChange: (user_id | null) => void
 *   disabled?: boolean
 *   testid?: string
 *   label?: string  (text shown when no one assigned, default "Assign")
 */
export default function AssigneePicker({
  value,
  onChange,
  disabled = false,
  testid,
  label = "Assign",
  compact = false,
}) {
  const members = useTeamMembers();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const selected = members.find((m) => m.user_id === value) || null;

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        onMouseDown={(e) => e.stopPropagation()}
        draggable={false}
        data-testid={testid || "assignee-picker"}
        className={`inline-flex items-center gap-1.5 rounded-full transition ${
          compact ? "px-1.5 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"
        } ${
          selected
            ? "bg-[var(--subtle)] text-[var(--ink)] border border-[var(--border)] hover:border-[var(--ink)]"
            : "border border-dashed border-[var(--border)] text-[var(--ink-3)] hover:text-[var(--ink)] hover:border-[var(--ink)]"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        title={selected ? `Assigned to ${selected.name}` : label}
      >
        <AssigneeAvatar user={selected} size={compact ? 16 : 18} />
        <span className="truncate max-w-[120px]">
          {selected ? selected.name : label}
        </span>
        {!compact && <ChevronDown size={11} className="opacity-60" />}
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 min-w-[220px] bg-[var(--paper)] border border-[var(--border)] rounded-md shadow-lg py-1"
          style={{ right: 0 }}
          data-testid={`${testid || "assignee-picker"}-menu`}
        >
          <button
            type="button"
            onClick={() => { onChange(null); setOpen(false); }}
            className="w-full text-left px-3 py-1.5 text-[12px] flex items-center gap-2 text-[var(--ink-3)] hover:bg-[var(--subtle)]"
            data-testid="assignee-picker-clear"
          >
            <X size={12} /> Unassign
          </button>
          <div className="border-t border-[var(--border)] my-1" />
          {members.length === 0 && (
            <div className="px-3 py-2 text-[11px] text-[var(--ink-3)] font-mono">
              No teammates yet — invite someone from /team.
            </div>
          )}
          {members
            .filter((m) => m.is_active !== false)
            .map((m) => (
              <button
                type="button"
                key={m.user_id}
                onClick={() => { onChange(m.user_id); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-[12px] flex items-center gap-2 hover:bg-[var(--subtle)] ${
                  value === m.user_id ? "bg-[var(--subtle)]" : ""
                }`}
                data-testid={`assignee-picker-option-${m.user_id}`}
              >
                <AssigneeAvatar user={m} size={18} />
                <span className="flex-1">
                  <span className="text-[var(--ink)]">{m.name}</span>
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
                    {m.role}
                  </span>
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
